import { Modal } from 'antd'
import axios from 'axios'
import { isArray, isUndefined } from 'lodash-es'
import { QueryClient } from 'react-query'

import { attachActivityTracking } from '../activity/activityRequestInterceptor'
import { setActivityDevWarnings } from '../activity/devWarnings'

import { errorResponseInterceptor } from './errorResponseInterceptor'
import localAccountManager from './localAccountManager'
import * as authService from './oauthService'
import { refreshTokenAcrossTabs } from './tokenRefreshLock'

export let API_ACCESS_TOKEN = ''

export const GetAccessToken = () => {
  return API_ACCESS_TOKEN
}

if (import.meta.env.DEV) {
  API_ACCESS_TOKEN = sessionStorage.getItem('access_token') || ''
}

const headerConfig = {
  Authorization: 'Bearer ' + API_ACCESS_TOKEN,
  'X-Requested-With': 'XMLHttpRequest',
}

export const ApiRequest = axios.create({
  headers: headerConfig,
  baseURL: process.env.VITE_API_URL + '/api',
})

export const AppRequest = axios.create({
  headers: headerConfig,
  baseURL: process.env.VITE_API_URL + '/api/app',
})

export const RootRequest = axios.create({
  headers: headerConfig,
  baseURL: process.env.VITE_API_URL + '/',
})

// Activity history: every request carries the X-Activity-* headers of the user action in
// progress. Attached before the other interceptors, so its response side runs before the token
// refresh retry and the error toast (see attachActivityTracking).
setActivityDevWarnings(!!import.meta.env.DEV)
;[ApiRequest, AppRequest, RootRequest].forEach(instance =>
  attachActivityTracking(instance)
)

// Define a function to refresh the access token
async function refreshAccessToken() {
  // Make a request to your backend to refresh the token
  // // await userManager.signinSilent()
  const resp = await RootRequest.post(
    '/connect/token',
    {
      refresh_token: localAccountManager.getRefreshToken(), // Send the refresh token
      grant_type: 'refresh_token',
      client_id: 'ICS_App',
    },
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    }
  )

  return resp.data
}

// ─── Session timing configuration ─────────────────────────────────────────
// Defaults (seconds). Overridden at runtime by initSessionConfig() using
// the tenant settings AuthServer:RefreshTokenLifetimeMinutes and
// AuthServer:AccessTokenLifetimeMinutes.
// How long (seconds) the server keeps a session alive without activity
let AUTO_LOGOUT_SECONDS = 3600
// The token's own lifetime (seconds) as issued by the auth server
let TOKEN_EXPIRE_SECONDS = 1800
// How many seconds before the effective session end to show the warning
let WARN_BEFORE_SECONDS = 180
// Minimum gap (seconds) WARN_BEFORE_SECONDS must stay below TOKEN_EXPIRE_SECONDS.
// If violated, TOKEN_EXPIRE_SECONDS is bumped up so the warning still has room
// to fire before the access token expires.
const MIN_WARN_TOKEN_GAP_SECONDS = 60
// ──────────────────────────────────────────────────────────────────────────

// ─── Test override (manual QA only) ───────────────────────────────────────
// Persists short timing values in localStorage so the watcher's warning →
// expired flow can be exercised within seconds instead of the production
// 30/60-minute defaults. Activate from the browser devtools console:
//
//   __icsTestSessionExpiry({ sessionLifetimeSec: 90, warnBeforeSec: 30 })
//   __icsClearSessionTest()
//
// `sessionLifetimeSec` is snapshotted to an absolute expiry timestamp the
// moment the override is set, so the remaining time counts DOWN correctly
// across re-arms (visibility change, cross-tab refresh, etc.).
// `warnBeforeSec` overrides WARN_BEFORE_SECONDS.
// Override persists across reloads until cleared.
const SESSION_TEST_OVERRIDE_KEY = 'ICS_SESSION_TEST_OVERRIDE'

// Stored shape. `sessionLifetimeSec` (the original duration) is kept so we
// can re-snapshot `effectiveExpiryAt` whenever a real refresh succeeds —
// otherwise the override would keep using the original timestamp and fire
// the warning again immediately after Continue.
interface SessionTestOverrideStored {
  sessionLifetimeSec?: number // duration; used to re-snapshot on refresh
  effectiveExpiryAt?: number // Unix seconds when test "session" ends
  warnBeforeSec?: number
}

function readSessionTestOverride(): SessionTestOverrideStored | null {
  try {
    const raw = localStorage.getItem(SESSION_TEST_OVERRIDE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') {
      return null
    }
    const result: SessionTestOverrideStored = {}
    if (
      typeof parsed.sessionLifetimeSec === 'number' &&
      parsed.sessionLifetimeSec > 0
    ) {
      result.sessionLifetimeSec = parsed.sessionLifetimeSec
    }
    if (
      typeof parsed.effectiveExpiryAt === 'number' &&
      parsed.effectiveExpiryAt > 0
    ) {
      result.effectiveExpiryAt = parsed.effectiveExpiryAt
    }
    if (typeof parsed.warnBeforeSec === 'number' && parsed.warnBeforeSec > 0) {
      result.warnBeforeSec = parsed.warnBeforeSec
    }
    return Object.keys(result).length > 0 ? result : null
  } catch {
    return null
  }
}

// Re-snapshot the test override's `effectiveExpiryAt` from `sessionLifetimeSec`.
// Called after a successful real-token refresh (Continue or silent 401-retry)
// so the warning cycle resets cleanly instead of firing again immediately
// against the old absolute timestamp.
function refreshTestOverrideExpiry() {
  const override = readSessionTestOverride()
  if (!override || !override.sessionLifetimeSec) {
    return
  }
  const updated: SessionTestOverrideStored = {
    ...override,
    effectiveExpiryAt:
      Math.floor(Date.now() / 1000) + override.sessionLifetimeSec,
  }
  try {
    localStorage.setItem(SESSION_TEST_OVERRIDE_KEY, JSON.stringify(updated))
    console.warn(
      `[SessionTest] Re-snapshotted expiry after refresh: +${override.sessionLifetimeSec}s`
    )
  } catch {
    // localStorage write failure — leave override unchanged
  }
}
// ──────────────────────────────────────────────────────────────────────────

/**
 * Apply session timing values from the current tenant settings object.
 * Call this after tenant data has been loaded (e.g. in appStore.init).
 *
 * Priority (per-tenant token lifetime settings win over legacy AuthServer keys):
 *   ICS.TokenLifetime.RefreshTokenMinutes       → AUTO_LOGOUT_SECONDS  (sliding idle window)
 *   ICS.TokenLifetime.AccessTokenMinutes         → TOKEN_EXPIRE_SECONDS (bearer token lifetime)
 *
 * Legacy fallback (used when ICS.TokenLifetime keys are absent):
 *   AuthServer:RefreshTokenLifetimeMinutes       → AUTO_LOGOUT_SECONDS
 *   AuthServer:AccessTokenLifetimeMinutes        → TOKEN_EXPIRE_SECONDS
 */
export function initSessionConfig(settings: Record<string, any> = {}) {
  const refreshMinutes = parseFloat(
    settings['ICS.TokenLifetime.RefreshTokenMinutes'] ??
      settings['AuthServer:RefreshTokenLifetimeMinutes']
  )
  const accessMinutes = parseFloat(
    settings['ICS.TokenLifetime.AccessTokenMinutes'] ??
      settings['AuthServer:AccessTokenLifetimeMinutes']
  )

  if (!isNaN(refreshMinutes) && refreshMinutes > 0) {
    AUTO_LOGOUT_SECONDS = refreshMinutes * 60
  }
  if (!isNaN(accessMinutes) && accessMinutes > 0) {
    TOKEN_EXPIRE_SECONDS = accessMinutes * 60
  }

  if (TOKEN_EXPIRE_SECONDS - WARN_BEFORE_SECONDS < MIN_WARN_TOKEN_GAP_SECONDS) {
    const adjusted = WARN_BEFORE_SECONDS + MIN_WARN_TOKEN_GAP_SECONDS
    console.warn(
      `[SessionConfig] TOKEN_EXPIRE_SECONDS (${TOKEN_EXPIRE_SECONDS}s) too close to WARN_BEFORE_SECONDS (${WARN_BEFORE_SECONDS}s); bumping to ${adjusted}s to preserve ${MIN_WARN_TOKEN_GAP_SECONDS}s gap.`
    )
    TOKEN_EXPIRE_SECONDS = adjusted
  }

  console.log(
    `[SessionConfig] AUTO_LOGOUT=${AUTO_LOGOUT_SECONDS}s, TOKEN_EXPIRE=${TOKEN_EXPIRE_SECONDS}s, GRACE=${
      AUTO_LOGOUT_SECONDS - TOKEN_EXPIRE_SECONDS
    }s`
  )
}

let sessionWatcherTimer: ReturnType<typeof setTimeout> | null = null

// ─── Cross-tab session sync ────────────────────────────────────────────────
const sessionChannel =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('ics-session')
    : null

// Module-level refs so any tab event can close active modals
let activeExpiredModalRef: { destroy: () => void } | null = null
let activeWarningModalRef: { destroy: () => void } | null = null

function dismissSessionModals() {
  const warningRef = activeWarningModalRef
  activeWarningModalRef = null
  if (warningRef) {
    warningRef.destroy()
  }

  const expiredRef = activeExpiredModalRef
  activeExpiredModalRef = null
  if (expiredRef) {
    expiredRef.destroy()
  }
}
// ──────────────────────────────────────────────────────────────────────────────

const showSessionExpiredModal = () => {
  if (activeExpiredModalRef) {
    return
  }
  const ref = Modal.error({
    title: 'Session Expired',
    content:
      'Your session has expired due to exceeding inactivity or authorisation lifetime. Please sign in again to continue.',
    okText: 'Sign In',
    onOk: () => {
      activeExpiredModalRef = null
      authService.logout()
    },
  })
  activeExpiredModalRef = ref
}

const performTokenRefresh = async (): Promise<string | undefined> => {
  // Access token has expired; refresh it
  try {
    const data = await refreshAccessToken()
    // Update expireAt on the current account so the watcher uses the new expiry time
    if (data?.expires_in && localAccountManager.currentAccount) {
      localAccountManager.currentAccount.expireAt =
        Math.floor(Date.now() / 1000) + data.expires_in
    }
    // If a test override is active, re-snapshot its expiry timestamp from the
    // stored duration. Otherwise the warning modal would fire again
    // immediately after Continue (we'd still be using the original snapshot).
    refreshTestOverrideExpiry()
    localAccountManager.updateOidcUser(data)
    setAuthHeader(data?.access_token)
    // Notify other tabs so they can sync the new token immediately
    sessionChannel?.postMessage({
      type: 'TOKEN_REFRESHED',
      accessToken: data?.access_token,
      expiresIn: data?.expires_in,
    })
    // Restart the session expiry watcher with the new token's expiry
    startSessionExpiryWatcher()
    return data?.access_token
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      clearSessionExpiryWatcher()
      showSessionExpiredModal()
    }
    throw error
  }
}

// Refresh tokens rotate with zero reuse leeway on the server, so redeeming the
// same one from two tabs revokes the whole session. Serialise the exchange
// across tabs and, once the lock is ours, reuse the token another tab may have
// obtained while we were waiting instead of redeeming a stale refresh token.
const handleRefreshToken = async _config => {
  return refreshTokenAcrossTabs({
    getHeld: () => ({
      refreshToken: localAccountManager.getRefreshToken(),
      tenantId: localAccountManager.currentAccount?.tenantId,
      userName: localAccountManager.currentAccount?.userName,
    }),
    readStoredAccount: () => localStorage.getItem('currentAccount'),
    syncFromStorage: syncAccountFromStorage,
    adopt: accessToken => {
      setAuthHeader(accessToken)
      dismissSessionModals()
      startSessionExpiryWatcher()
    },
    refresh: performTokenRefresh,
  })
}

// ─── Session Expiry Watcher ────────────────────────────────────────────────

function clearSessionExpiryWatcher() {
  if (sessionWatcherTimer !== null) {
    clearTimeout(sessionWatcherTimer)
    sessionWatcherTimer = null
  }
}

function showSessionExpiryWarning(initialSeconds: number) {
  const expiresAtMs = Date.now() + Math.max(0, initialSeconds) * 1000
  let remaining = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))
  let lastRenderedRemaining = remaining
  let countdownInterval: ReturnType<typeof setInterval> | null = null

  const getContent = (secs: number) =>
    `Your session will expire in ${secs} second${
      secs !== 1 ? 's' : ''
    }. Click Continue to stay signed in.`

  const stopCountdown = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval)
      countdownInterval = null
    }
    document.removeEventListener('visibilitychange', syncCountdown)
  }

  const modal = Modal.confirm({
    title: 'Session Expiring Soon',
    content: getContent(remaining),
    okText: 'Continue',
    cancelText: 'Sign Out',
    onOk: async () => {
      stopCountdown()
      activeWarningModalRef = null
      try {
        // Shared single-flight: a 401 retry already refreshing in this tab
        // must not be doubled by the Continue button.
        await refreshAccessTokenShared()
      } catch {
        // Refresh failed (session already expired during sleep, network
        // error, etc.). handleRefreshToken's catch will have surfaced the
        // expired modal for the 400 case; for any other failure fall back
        // to the same modal so the user gets a clear "session ended, sign
        // in again" message instead of the generic error toast.
        // showSessionExpiredModal is idempotent — no-op if already shown.
        showSessionExpiredModal()
        // Swallow the rejection so AntD closes this warning modal cleanly;
        // re-throwing would keep it stacked on top of the expired modal.
      }
    },
    onCancel: () => {
      stopCountdown()
      activeWarningModalRef = null
      authService.logout()
    },
  })

  activeWarningModalRef = {
    destroy: () => {
      stopCountdown()
      activeWarningModalRef = null
      modal.destroy()
    },
  }

  const syncCountdown = () => {
    remaining = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000))

    if (remaining <= 0) {
      stopCountdown()
      activeWarningModalRef = null
      modal.destroy()
      showSessionExpiredModal()
      return
    }

    if (remaining !== lastRenderedRemaining) {
      lastRenderedRemaining = remaining
      modal.update({ content: getContent(remaining) })
    }
  }

  countdownInterval = setInterval(syncCountdown, 1000)

  // Timers are throttled in background tabs; force re-sync when tab becomes visible.
  document.addEventListener('visibilitychange', syncCountdown)
}

export function startSessionExpiryWatcher() {
  clearSessionExpiryWatcher()

  // A session modal is already on screen — its own countdown / visibility
  // handler keeps it accurate. Re-arming here would race with that and could
  // either dismiss the active modal or stack a second one on top.
  if (activeWarningModalRef || activeExpiredModalRef) {
    return
  }

  const expireAt = localAccountManager.currentAccount?.expireAt
  if (!expireAt) {
    return
  }

  const now = Math.floor(Date.now() / 1000)
  const testOverride = readSessionTestOverride()
  const effectiveWarnBefore = testOverride?.warnBeforeSec ?? WARN_BEFORE_SECONDS

  let secondsUntilEffectiveExpiry: number
  if (testOverride?.effectiveExpiryAt) {
    // Test mode: an absolute "fake expiry" was snapshotted when the override
    // was set. Compute remaining time against that fixed timestamp so the
    // countdown progresses correctly across re-arms (visibility change,
    // cross-tab refresh, etc.) instead of resetting to the full lifetime.
    secondsUntilEffectiveExpiry = testOverride.effectiveExpiryAt - now
    console.warn(
      `[SessionWatcher] TEST OVERRIDE active: ${secondsUntilEffectiveExpiry}s until fake expiry (warn-before=${effectiveWarnBefore}s)`
    )
  } else {
    // The server keeps the session alive for AUTO_LOGOUT_SECONDS total, but
    // the token itself only lives TOKEN_EXPIRE_SECONDS. The difference is the
    // grace period after token expiry before the server truly logs the user out.
    const SESSION_GRACE_SECONDS = AUTO_LOGOUT_SECONDS - TOKEN_EXPIRE_SECONDS
    const effectiveExpiry = expireAt + SESSION_GRACE_SECONDS
    secondsUntilEffectiveExpiry = effectiveExpiry - now
  }

  // Session already over by the time we arm (browser restart, long sleep,
  // page reload past expiry, etc.). Surface the expired modal immediately
  // instead of waiting for the next API call to fail.
  if (secondsUntilEffectiveExpiry <= 0) {
    console.log(
      '[SessionWatcher] Session already expired on arm. Showing expired modal now.'
    )
    showSessionExpiredModal()
    return
  }

  // Warning should fire exactly `effectiveWarnBefore` seconds before effective
  // expiry. If the remaining session is already <= effectiveWarnBefore at arm
  // time (page reload near expiry, post-refresh re-arm with short session,
  // etc.), we've missed the lead window — show the warning immediately and
  // let the countdown use whatever time is actually left.
  const secondsUntilWarn = secondsUntilEffectiveExpiry - effectiveWarnBefore

  if (secondsUntilWarn <= 0) {
    console.log(
      `[SessionWatcher] Within warning window (${secondsUntilEffectiveExpiry}s remain). Showing warning now.`
    )
    showSessionExpiryWarning(secondsUntilEffectiveExpiry)
    return
  }

  console.log(
    `[SessionWatcher] Warning modal will appear in ${secondsUntilWarn}s (effective expiry in ${secondsUntilEffectiveExpiry}s, warn-before=${effectiveWarnBefore}s).`
  )
  sessionWatcherTimer = setTimeout(() => {
    showSessionExpiryWarning(effectiveWarnBefore)
  }, secondsUntilWarn * 1000)
}

// Pull the latest currentAccount (and crucially its oidcUser, which holds the
// active refresh_token) from localStorage into this tab's in-memory state.
// Needed because:
//   * IdentityServer rotates refresh_tokens on every refresh — the old one is
//     invalidated, so a stale in-memory copy will fail the next refresh with
//     a 400.
//   * Another tab may have performed that refresh while this tab was hidden
//     or while its BroadcastChannel was throttled.
function syncAccountFromStorage() {
  if (!localAccountManager.currentAccount) {
    return
  }
  try {
    const raw = localStorage.getItem('currentAccount')
    if (!raw) {
      return
    }
    const persisted = JSON.parse(raw)
    if (!persisted || typeof persisted !== 'object') {
      return
    }
    if (persisted.oidcUser) {
      localAccountManager.currentAccount.oidcUser = persisted.oidcUser
    }
    if (typeof persisted.expireAt === 'number') {
      localAccountManager.currentAccount.expireAt = persisted.expireAt
    }
  } catch {
    // localStorage parse failure — leave in-memory state alone
  }
}

// Re-arm the watcher whenever the tab becomes visible again. Browsers throttle
// (and sometimes coalesce) setTimeout in background tabs, so the originally
// scheduled warning can fire late — or not at all if the tab was suspended for
// long enough. Re-arming on visibility ensures the warning / expired modal
// always shows at the correct moment, including the "user came back hours
// later, session is already over" case. Also pull the latest account state
// from localStorage so a refresh performed by another tab is picked up here.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      return
    }
    if (!localAccountManager.currentAccount?.expireAt) {
      return
    }
    syncAccountFromStorage()
    startSessionExpiryWatcher()
  })
}

// ─── Devtools shortcuts for session test override ──────────────────────────
// Exposed on `window` so QA / devs can drive the warning + expired flow on
// demand from the browser console — no need to wait for the real 30/60-min
// timers or edit localStorage by hand.
//
//   __icsTestSessionExpiry(60)            // session "ends" in 60s, default 180s warn-before
//   __icsTestSessionExpiry(60, 20)        // session ends in 60s, warn 20s before (= at 40s)
//   __icsTestSessionExpiry({ sessionLifetimeSec: 90, warnBeforeSec: 30 })
//   __icsClearSessionTest()               // remove override
//
// The override is persisted to localStorage, so it survives page reloads
// (and cross-tab) until you explicitly clear it. Calling the setter also
// re-arms the watcher immediately so you don't have to refresh to see the
// new behaviour.
interface SessionTestOverrideInput {
  // Duration form — gets snapshotted to absolute timestamp at set time.
  sessionLifetimeSec?: number
  warnBeforeSec?: number
}

if (typeof window !== 'undefined') {
  ;(window as any).__icsTestSessionExpiry = (
    arg1?: number | SessionTestOverrideInput,
    arg2?: number
  ) => {
    const input: SessionTestOverrideInput =
      typeof arg1 === 'number'
        ? { sessionLifetimeSec: arg1, warnBeforeSec: arg2 }
        : { ...(arg1 ?? {}) }

    if (!input.sessionLifetimeSec && !input.warnBeforeSec) {
      console.warn(
        '[SessionTest] No override fields provided. Use { sessionLifetimeSec, warnBeforeSec }.'
      )
      return
    }

    // Snapshot the duration to an absolute Unix-second expiry so the watcher
    // counts DOWN across re-arms instead of resetting to the full lifetime
    // every time (e.g. on visibility change or cross-tab refresh).
    // Also persist the original duration so refreshTestOverrideExpiry() can
    // re-snapshot after a successful Continue / silent refresh.
    const stored: SessionTestOverrideStored = {}
    if (input.sessionLifetimeSec) {
      stored.sessionLifetimeSec = input.sessionLifetimeSec
      stored.effectiveExpiryAt =
        Math.floor(Date.now() / 1000) + input.sessionLifetimeSec
    }
    if (input.warnBeforeSec) {
      stored.warnBeforeSec = input.warnBeforeSec
    }

    try {
      localStorage.setItem(SESSION_TEST_OVERRIDE_KEY, JSON.stringify(stored))
    } catch (e) {
      console.error('[SessionTest] Failed to persist override', e)
      return
    }
    console.warn('[SessionTest] Override set:', { input, stored })
    // Re-arm so the override takes effect right now.
    if (activeWarningModalRef || activeExpiredModalRef) {
      dismissSessionModals()
    }
    startSessionExpiryWatcher()
  }
  ;(window as any).__icsClearSessionTest = () => {
    localStorage.removeItem(SESSION_TEST_OVERRIDE_KEY)
    console.warn('[SessionTest] Override cleared. Re-arming watcher.')
    if (activeWarningModalRef || activeExpiredModalRef) {
      dismissSessionModals()
    }
    startSessionExpiryWatcher()
  }
}

// ──────────────────────────────────────────────────────────────────────────────

let refreshingTokenPromise: Promise<string | undefined> | null = null

/**
 * Refreshes the access token once for all concurrent callers. For requests that do not go
 * through the axios 401 interceptor (e.g. the assistant's fetch-based SSE stream).
 */
export const refreshAccessTokenShared = (): Promise<string | undefined> => {
  if (!refreshingTokenPromise) {
    refreshingTokenPromise = handleRefreshToken(ApiRequest).finally(() => {
      refreshingTokenPromise = null
    })
  }
  return refreshingTokenPromise
}

const handleRefreshExpiredToken = async error => {
  const originalRequest = error.config
  // Check if the error status code is 401 (unauthorized) and handle token refresh
  if (error.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true

    try {
      const accessToken = await refreshAccessTokenShared()
      originalRequest.headers.Authorization = 'Bearer ' + accessToken
      // Retry the original request with the refreshed token
      return ApiRequest(originalRequest)
    } catch (refreshError) {
      // Handle refresh token failure (e.g., logout the user)
      return Promise.reject(refreshError)
    }
  }

  return Promise.reject(error)
}

// Add a request interceptor
ApiRequest.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    return handleRefreshExpiredToken(error)
  }
)

AppRequest.interceptors.response.use(
  response => {
    return response
  },
  async error => {
    return handleRefreshExpiredToken(error)
  }
)

const successResponseInterceptor = resp => {
  return resp
}

ApiRequest.interceptors.response.use(
  successResponseInterceptor,
  errorResponseInterceptor
)
AppRequest.interceptors.response.use(
  successResponseInterceptor,
  errorResponseInterceptor
)
RootRequest.interceptors.response.use(
  successResponseInterceptor,
  errorResponseInterceptor
)

const accessTokenListeners = new Set<() => void>()

/**
 * Subscribe to access-token changes (login, refresh, another tab's refresh). For long-lived
 * connections such as SignalR that cannot ride the axios 401 retry. Returns an unsubscribe.
 */
export const onAccessTokenChanged = (listener: () => void) => {
  accessTokenListeners.add(listener)
  return () => {
    accessTokenListeners.delete(listener)
  }
}

/**
 * True once the current access token is past its expiry. Unknown expiry counts as not expired.
 * Deliberately does not refresh: only user activity may extend the session (idle logout).
 */
export const isAccessTokenExpired = () => {
  const expireAt = localAccountManager.currentAccount?.expireAt
  return !!expireAt && expireAt <= Math.floor(Date.now() / 1000)
}

export const setAuthHeader = token => {
  ApiRequest.defaults.headers.Authorization = 'Bearer ' + token
  AppRequest.defaults.headers.Authorization = 'Bearer ' + token
  RootRequest.defaults.headers.Authorization = 'Bearer ' + token

  API_ACCESS_TOKEN = token

  if (token) {
    accessTokenListeners.forEach(listener => listener())
  }

  if (import.meta.env.DEV) {
    sessionStorage.setItem('access_token', token)
  } else {
  }
}

export const clearAuthHeader = () => {
  ApiRequest.defaults.headers.Authorization = undefined
  AppRequest.defaults.headers.Authorization = undefined
  RootRequest.defaults.headers.Authorization = undefined
}

// ─── Cross-tab token refresh listener ────────────────────────────────────────
// When another tab refreshes the token, sync auth headers, dismiss stale modals,
// and restart the session watcher in this tab.
if (sessionChannel) {
  sessionChannel.onmessage = event => {
    const msg = event.data
    if (msg?.type === 'TOKEN_REFRESHED') {
      // Pull the freshly persisted oidcUser (refresh_token + expireAt) from
      // localStorage. The other tab wrote it BEFORE postMessage'ing, so by
      // the time we receive this it's guaranteed to be there. Without this
      // step, this tab's in-memory refresh_token stays stale and the next
      // refresh attempt fails with 400 even though the session is healthy.
      syncAccountFromStorage()
      if (msg.expiresIn && localAccountManager.currentAccount) {
        localAccountManager.currentAccount.expireAt =
          Math.floor(Date.now() / 1000) + msg.expiresIn
      }
      setAuthHeader(msg.accessToken)
      dismissSessionModals()
      startSessionExpiryWatcher()
    }
  }
}
// ──────────────────────────────────────────────────────────────────────────────

export const queryClient = new QueryClient()

export interface QueryOptions {
  disableCache?: boolean
  /**
   * Cache time in seconds
   */
  staleTime?: number
}
export function CreateParams(params) {
  let qs = []

  Object.keys(params).forEach(key => {
    const value = params[key]

    if (isArray(value)) {
      value.forEach(i => {
        qs.push(`${key}=${i}`)
      })
    } else {
      if (value || typeof value === 'boolean') {
        qs.push(`${key}=${isUndefined(value) ? '' : value}`)
      }
    }
  })
  return qs
}

export function GetBaseServiceFunctions(baseRoute) {
  async function GetAll(params: any = {}, options?: QueryOptions) {
    if (params && !params.maxResultCount) {
      params.maxResultCount = window.APP_CONFIG.maxResultCount
    }

    if (options?.disableCache) {
      await queryClient.invalidateQueries([baseRoute])
    }
    return queryClient.fetchQuery(
      [baseRoute, params],
      () => {
        return ApiRequest.get(baseRoute, {
          params: params,
          paramsSerializer: {
            indexes: true,
          },
        })
      },
      {
        staleTime: (options?.staleTime || 60) * 1000,
      }
    )
  }

  async function GetById(id, options?: QueryOptions) {
    if (options?.disableCache) {
      await queryClient.invalidateQueries([baseRoute])
    }
    return queryClient.fetchQuery(
      [baseRoute, id],
      () => {
        return ApiRequest.get(`${baseRoute}/${id}`)
      },
      {
        staleTime: 60000,
      }
    )
  }

  function GetMyDetail() {
    return ApiRequest.get(`${baseRoute}/myDetail`)
  }

  async function Create(data) {
    const resp = await ApiRequest.post(baseRoute, data)
    await queryClient.invalidateQueries([baseRoute])
    return resp
  }

  async function Update(id, data) {
    await queryClient.invalidateQueries([baseRoute, id])
    await queryClient.invalidateQueries([baseRoute])
    const resp = await ApiRequest.put(`${baseRoute}/${id}`, data)
    return resp
  }

  // async function UpdateEmailUsername(id, data) {
  //   await queryClient.invalidateQueries([baseRoute, id])
  //   await queryClient.invalidateQueries([baseRoute])
  //   const resp = await ApiRequest.put(
  //     `${baseRoute}/UpdateEmailUsername/${id}`,
  //     data
  //   )
  //   return resp
  // }

  async function Delete(id) {
    const resp = await ApiRequest.delete(`${baseRoute}/${id}`)
    await queryClient.invalidateQueries([baseRoute])
    return resp
  }

  return {
    GetAll: GetAll,
    GetById: GetById,
    GetMyDetail: GetMyDetail,
    Create: Create,
    Update: Update,
    Delete: Delete,
  }
}
